import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Sparkles, Mail, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extract subdomain from current URL
const getSubdomain = () => {
  const host = window.location.hostname;
  const parts = host.split('.');
  
  // Skip preview/emergentagent domains - these are not tenant subdomains
  if (host.includes('preview.emergentagent.com') || host.includes('emergentagent.com')) {
    return null;
  }
  
  // Skip localhost
  if (host === 'localhost' || host.startsWith('127.') || host.startsWith('192.168.')) {
    return null;
  }
  
  // Check if it's a subdomain (e.g., nirvaana.wellnesttechnologies.com)
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip reserved subdomains
    if (!['www', 'app', 'api', 'admin', 'preview'].includes(subdomain)) {
      return subdomain;
    }
  }
  return null;
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'staff'
  
  // Tenant branding state
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const subdomain = getSubdomain();

  useEffect(() => {
    if (subdomain) {
      fetchTenantInfo();
    } else {
      setLoadingTenant(false);
    }
  }, [subdomain]);

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API}/tenant/by-subdomain/${subdomain}`);
      setTenantInfo(response.data);
    } catch (error) {
      console.error('Tenant not found:', error);
      // If tenant not found and we're on a real subdomain (not preview), redirect to main login
      if (error.response?.status === 404) {
        const host = window.location.hostname;
        // Don't redirect if on preview/emergentagent domain
        if (!host.includes('emergentagent.com') && !host.includes('localhost')) {
          toast.error('Invalid tenant. Redirecting to main login...');
          setTimeout(() => {
            window.location.href = window.location.origin + '/login';
          }, 2000);
        }
      }
    } finally {
      setLoadingTenant(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If staff login type, try shared therapist/hotel login first
      if (loginType === 'staff') {
        try {
          const sharedResponse = await axios.post(`${API}/therapist-auth/login`, { 
            username: email, 
            password 
          });
          
          // Check if company is suspended
          if (sharedResponse.data.company_status === 'suspended' || sharedResponse.data.company_status === 'expired') {
            toast.error('Your organization\'s account is suspended. Please contact your administrator.');
            setLoading(false);
            return;
          }
          
          toast.success('Welcome to Staff Portal');
          const sharedUser = {
            ...sharedResponse.data.user,
            role: sharedResponse.data.user.role || 'therapist',
            assigned_property_id: sharedResponse.data.user.property_id,
            full_name: sharedResponse.data.user.full_name || sharedResponse.data.user.property_name,
            login_type: 'shared',
            operations_access: sharedResponse.data.user.operations_access || true,
            pin_required_for: sharedResponse.data.user.pin_required_for || ['attendance', 'leave_request', 'personal_profile'],
            therapists: sharedResponse.data.therapists || []
          };
          onLogin(sharedUser, sharedResponse.data.token);
          return;
        } catch (sharedError) {
          toast.error(sharedError.response?.data?.detail || 'Invalid staff credentials');
          setLoading(false);
          return;
        }
      }
      
      // Admin login
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const user = response.data.user;
      const subscription = response.data.subscription;
      
      // Check if it's a super admin trying to login here
      if (user.role === 'platform_super_admin') {
        toast.error('Platform administrators cannot login here. Use the admin portal.');
        setLoading(false);
        return;
      }
      
      // If on subdomain, verify user belongs to this tenant
      if (subdomain && tenantInfo && user.company_id !== tenantInfo.company_id) {
        toast.error('This account does not belong to this organization');
        setLoading(false);
        return;
      }
      
      // Check subscription status
      if (response.data.requires_subscription) {
        localStorage.setItem('subscription_status', JSON.stringify(subscription));
        toast.warning('Your subscription has expired. Please renew to continue using all features.');
      } else if (subscription?.days_remaining && subscription.days_remaining <= 3) {
        toast.info(`Your subscription expires in ${subscription.days_remaining} days. Consider renewing soon.`);
      } else {
        toast.success(`Welcome to ${tenantInfo?.company_name || 'your Wellness Dashboard'}`);
      }
      
      // Store subdomain info for routing
      if (tenantInfo) {
        localStorage.setItem('tenant_subdomain', subdomain);
        localStorage.setItem('tenant_info', JSON.stringify(tenantInfo));
      }
      
      onLogin(user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = () => {
    // Navigate to onboarding page in same environment (preview or production)
    window.location.href = '/onboarding';
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If this email exists, a new password has been sent to your inbox');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Failed to send password reset email. Please try again.');
    } finally {
      setSendingReset(false);
    }
  };

  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-950">
      {/* Left Panel - Branding */}
      <div 
        className="lg:w-1/2 h-64 lg:h-screen relative flex items-center justify-center p-8"
        style={{
          background: tenantInfo 
            ? `linear-gradient(135deg, ${tenantInfo.primary_color}20 0%, ${tenantInfo.secondary_color}20 100%)`
            : 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
        }}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl" 
            style={{ backgroundColor: tenantInfo?.primary_color ? `${tenantInfo.primary_color}15` : '#14b8a615' }}
          />
          <div 
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl"
            style={{ backgroundColor: tenantInfo?.secondary_color ? `${tenantInfo.secondary_color}15` : '#06b6d415' }}
          />
        </div>
        
        <div className="relative z-10 text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            {tenantInfo?.logo_url ? (
              <img 
                src={tenantInfo.logo_url} 
                alt={tenantInfo.company_name}
                className="w-20 h-20 object-contain"
              />
            ) : (
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${tenantInfo?.primary_color || '#14b8a6'} 0%, ${tenantInfo?.secondary_color || '#06b6d4'} 100%)`,
                  boxShadow: `0 10px 25px ${tenantInfo?.primary_color || '#14b8a6'}40`
                }}
              >
                {tenantInfo ? (
                  <Building2 className="w-7 h-7 text-white" />
                ) : (
                  <Sparkles className="w-7 h-7 text-white" />
                )}
              </div>
            )}
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            {tenantInfo ? (
              tenantInfo.company_name
            ) : (
              <><span className="text-teal-400">WellNest</span> Technologies</>
            )}
          </h1>
          <p className="text-slate-400 text-lg mb-8">
            {tenantInfo ? 'Employee & Admin Portal' : 'Tenant Login Portal'}
          </p>
          
          {/* Subdomain indicator */}
          {subdomain && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-slate-300 text-sm mb-6">
              <Building2 className="w-4 h-4" />
              <span>{subdomain}.wellnesttechnologies.com</span>
            </div>
          )}
          
          {/* Feature highlights */}
          <div className="hidden lg:block space-y-4 text-left max-w-sm mx-auto">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-teal-400" />
              </div>
              <span>Manage bookings & staff</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-teal-400" />
              </div>
              <span>Track revenue & analytics</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-teal-400" />
              </div>
              <span>Multi-location support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-950">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-2">
              {tenantInfo ? `Sign in to ${tenantInfo.company_name}` : 'Tenant Login'}
            </h2>
            <p className="text-slate-400">
              {tenantInfo 
                ? 'Enter your credentials to access the dashboard'
                : 'Sign in to access your wellness business dashboard'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {/* Login Type Radio Buttons */}
            <div className="flex gap-4 p-1 bg-slate-900 rounded-xl">
              <button
                type="button"
                onClick={() => setLoginType('admin')}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginType === 'admin'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid="login-type-admin"
              >
                <div className="flex items-center justify-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span>Admin</span>
                </div>
                <span className="text-xs opacity-70 block mt-1">
                  Tenant Administrator
                </span>
              </button>
              <button
                type="button"
                onClick={() => setLoginType('staff')}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginType === 'staff'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid="login-type-staff"
              >
                <div className="flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Staff</span>
                </div>
                <span className="text-xs opacity-70 block mt-1">
                  Staff / Property Owners
                </span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <Input
                  type="text"
                  placeholder={loginType === 'admin' ? 'Email Address' : 'Username'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 rounded-lg bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 rounded-lg bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-lg text-base bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-semibold"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : loginType === 'admin' ? 'Sign In as Admin' : 'Sign In as Staff'}
            </Button>
          </form>

          <div className="text-center space-y-4">
            <button 
              onClick={() => setShowForgotPassword(true)}
              className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
            <p className="text-slate-500 text-sm">
              Don't have an account?{' '}
              <button 
                onClick={handleStartTrial}
                className="text-teal-400 hover:text-teal-300 font-medium"
              >
                Start Free Trial
              </button>
            </p>
            <p className="text-slate-600 text-xs">
              Need help? Contact support@wellnesttechnologies.com
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Reset Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your email address and we'll send you a new password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="pl-11 h-12 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                required
                data-testid="forgot-email-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingReset}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                data-testid="send-reset-button"
              >
                {sendingReset ? 'Sending...' : 'Send New Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
